import { assign, fromPromise, setup } from 'xstate';
import { openDeleteConfirmation, showAxiosErrorAction } from '../../actions';
import { facilityMachine } from '../facility/facility.machine';
import { createFacilityViewState, onFacilityFormOpen } from '../facility/subMachineHelper';
import {
  closeModal,
  deleteEmploymentFromContext,
  editEmployment,
  openModal,
  resetEmploymentState,
  resetLoading,
  setEmploymentIdToDelete,
  setEmploymentSort,
  toggleLoading,
  updateEmploymentFromCreate,
  updateEmploymentFromPatch,
  updateFacilityForm,
} from './actions';
import { API_CALLS } from './api';
import { emptyEmploymentForm } from './constants';
import {
  DeleteEmploymentEvent,
  Events,
  PatchEmploymentEvent,
  SubmitEmploymentEvent,
} from './events';
import { Context, EmploymentFormValues, Scope } from './types';

export const employmentMachine = setup({
  actors: {
    getEmployments: fromPromise(API_CALLS.get),
    createEmployment: fromPromise(API_CALLS.create),
    patchEmployment: fromPromise(API_CALLS.patch),
    deleteEmployment: fromPromise(API_CALLS.delete),
    facilityRef: facilityMachine,
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    toggleLoading: assign(toggleLoading),
    openModal: assign(openModal),
    closeModal: assign(closeModal),
    setEmploymentSort: assign(setEmploymentSort),
    updateFacilityForm: assign(updateFacilityForm),
    deleteEmploymentFromContext: assign(deleteEmploymentFromContext),
    resetLoading: assign(resetLoading),
    resetEmploymentState: assign(resetEmploymentState),
    editEmployment: assign(editEmployment),
    setEmploymentIdToDelete: assign(setEmploymentIdToDelete),
    updateEmploymentFromPatch: assign(updateEmploymentFromPatch),
    updateEmploymentFromCreate: assign(updateEmploymentFromCreate),
    sendError: showAxiosErrorAction,
    sendOpenDeleteConfirmation: openDeleteConfirmation,
  },
}).createMachine({
  context: ({ spawn }) => ({
    loading: false,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
    },
    modal: {
      edit: false,
      create: false,
    },
    facilityRef: spawn('facilityRef', {
      id: 'facilityStateEmployment',
      input: undefined,
    }),
    employments: [],
    employmentForm: emptyEmploymentForm,
    idToDelete: -1,
    sort: {
      sortBy: 'id',
      employer: -1,
      start_date: -1,
      title: -1,
      end_date: -1,
      id: -1,
      page: 1,
      pageSize: 100,
    },
  }),
  initial: '$_VIEW',
  on: {
    ON_OPEN_MODAL: {
      actions: { type: 'openModal' },
      target: '.$_FORM',
    },
    ON_CLOSE_MODAL: {
      actions: { type: 'closeModal' },
      target: '.$_VIEW',
    },
    ON_EDIT: {
      target: '.$_FORM',
      actions: ['editEmployment'],
    },
  },
  states: {
    $_VIEW: {
      entry: assign({
        loading: false,
      }),
      on: {
        ON_LOAD: {
          target: '$_GET',
          actions: assign({ loading: true }),
        },
        ON_SORT: {
          actions: 'setEmploymentSort',
          target: '$_GET',
        },
        ON_ADD: {
          actions: { type: 'openModal' },
          target: '$_FORM',
        },
        ON_DELETE: {
          actions: ['setEmploymentIdToDelete'],
          target: '$_SEND_DELETE',
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: ['sendOpenDeleteConfirmation'],
        },
        ON_GET: {
          actions: {
            type: 'toggleLoading',
          },
          target: `$_GET`,
        },
      },
    },
    $_FORM: {
      entry: [
        assign({ loading: false }),

        ({ context }) => {
          context.facilityRef.send({ type: 'ON_CLOSE' });
        },
      ],
      on: {
        ON_SUBMIT: {
          target: '$_SEND_CREATE',
        },
        ON_UPDATE: {
          target: '$_SEND_UPDATE',
        },
        ...onFacilityFormOpen(),
      },
    },
    $_SEND_CREATE: {
      invoke: {
        id: 'create',
        src: 'createEmployment',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: SubmitEmploymentEvent },
        onDone: {
          target: '$_VIEW',
          actions: ['updateEmploymentFromCreate', 'resetEmploymentState'],
        },
        onError: {
          target: '$_FORM',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_GET: {
      invoke: {
        id: 'get',
        src: 'getEmployments',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: [
            assign({
              employments: ({ event }) => event.output as unknown as EmploymentFormValues[],
            }),
            'resetEmploymentState',
          ],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetEmploymentState'],
        },
      },
    },
    $_SEND_DELETE: {
      invoke: {
        id: 'delete',
        src: 'deleteEmployment',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: DeleteEmploymentEvent },
        onDone: {
          target: '$_VIEW',
          actions: ['deleteEmploymentFromContext', 'resetLoading'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_SEND_UPDATE: {
      invoke: {
        id: 'patch',
        src: 'patchEmployment',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: PatchEmploymentEvent },
        onDone: {
          target: '$_VIEW',
          actions: ['updateEmploymentFromPatch', 'resetEmploymentState'],
        },
        onError: {
          target: '$_FORM',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    ...createFacilityViewState('$_FORM', ['updateFacilityForm']),
  },
});
